import Image from 'next/image';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { getFirstName, MarketingTeacher } from '../../../domain/teacher';
import { ExternalLink } from '../../common/externalLink';
import Spotify from '../../icons/spotify';

interface AboutProps {
  id: string;
  teacher: MarketingTeacher;
}

const About: React.FC<AboutProps> = ({ id, teacher }) => {
  const { website, instagram, instagramHandle, facebook, spotifyProfile } =
    teacher;

  const socialLinks = [
    {
      link: website,
      label: website
        ?.replace('https://', '')
        .replace('http://', '')
        .split('/')[0],
      svg: '/svg/web.svg',
    },
    {
      link: instagram,
      label: instagramHandle
        ? `@${instagramHandle?.replace('@', '')}`
        : instagram?.replace('https://www.', ''),
      svg: '/svg/instagram.svg',
    },
    {
      link: facebook,
      label: facebook?.replace('https://www.', ''),
      svg: '/svg/facebook.svg',
    },
    { link: spotifyProfile, label: 'Spotify', icon: Spotify },
  ].filter(link => !!link.link);

  return (
    <section id={id} className="grid grid-cols-1 md:grid-cols-3">
      <div className="order-2 text-white md:order-1 md:col-span-2 bg-background-dark content pt-32px pb-48px md:py-72px">
        <h2 className="text-3xl font-family-bold">
          About {getFirstName(teacher)}
        </h2>
        <div className="max-w-3xl prose prose-invert pt-16px">
          <ReactMarkdown rehypePlugins={[rehypeRaw]} skipHtml={false}>
            {teacher.bio}
          </ReactMarkdown>
        </div>

        {socialLinks.length > 0 && (
          <ul className="pt-32px space-y-16px">
            {socialLinks.map(link => (
              <li key={`about-social-${link.link}`}>
                <ExternalLink
                  key={link.link}
                  href={link.link}
                  className="flex flex-row items-center space-x-4 font-family-semibold"
                >
                  {link.icon && <link.icon classes="h-24px w-24px" />}
                  {link.svg && (
                    <Image
                      width="24"
                      height="24"
                      src={link.svg}
                      alt={teacher.name}
                    />
                  )}
                  <span className="hover:text-accent-hover">{link.label}</span>
                </ExternalLink>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="relative order-1 h-96 sm:order-1 md:order-1 md:h-auto">
        <Image
          src={`${teacher.profileImage.url}?w=1200&q=90`}
          className="object-cover w-full h-full"
          alt={teacher.name}
          layout="fill"
        />
      </div>
    </section>
  );
};

export default About;
